import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import 'foundation-sites/dist/css/foundation.min.css';
import CONSTANTS from '../../../assets/constants';
import Graphs from './graphs';
import infoIcon from '../../../assets/Images/infoIcon.svg';
import PropTypes from 'prop-types';
import AnswersContext from '../../context/answersContext';
import { AGE, EGFR } from './ChartRegion/helpers/graphConstants';

const FORM_DEFAULTS = {
  [CONSTANTS.REDUX.RISK]: '',
  [CONSTANTS.REDUX.TREATMENT_PLAN]: '',
  [CONSTANTS.REDUX.TREATMENT_TIMING]: 100,
  [CONSTANTS.REDUX.INITIAL_AGE]: 40,
};

const ClickableTooltip = ({ className = '', tooltipText, children }) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef(null);

  const toggleTooltip = (e) => {
    e.preventDefault();
    setVisible((prev) => !prev);
  };

  const handleClickOutside = useCallback(
    (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setVisible(false);
      }
    },
    [containerRef]
  );

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  }, [handleClickOutside]);

  return (
    <div
      ref={containerRef}
      className={`tooltip-container ${className}`}
      onClick={toggleTooltip}
    >
      {children}
      {visible && (
        <span className="tooltip-text" onClick={(e) => e.stopPropagation()}>
          {tooltipText}
        </span>
      )}
    </div>
  );
};

ClickableTooltip.propTypes = {
  className: PropTypes.string,
  tooltipText: PropTypes.object,
  children: PropTypes.node,
};

const ConditionalRadioForm = () => {
  const { answers, updateAnswers } = useContext(AnswersContext);

  const { control, watch } = useForm({
    defaultValues: {
      [CONSTANTS.REDUX.RISK]:
        answers[CONSTANTS.REDUX.RISK]?.value ||
        FORM_DEFAULTS[CONSTANTS.REDUX.RISK],
      [CONSTANTS.REDUX.TREATMENT_PLAN]:
        answers[CONSTANTS.REDUX.TREATMENT_PLAN]?.value ||
        FORM_DEFAULTS[CONSTANTS.REDUX.TREATMENT_PLAN],
      [CONSTANTS.REDUX.TREATMENT_TIMING]:
        answers[CONSTANTS.REDUX.TREATMENT_TIMING]?.value ||
        FORM_DEFAULTS[CONSTANTS.REDUX.TREATMENT_TIMING],
      [CONSTANTS.REDUX.INITIAL_AGE]:
        answers[CONSTANTS.REDUX.INITIAL_AGE]?.value ||
        FORM_DEFAULTS[CONSTANTS.REDUX.INITIAL_AGE],
    },
  });

  const question2Visible = answers[CONSTANTS.REDUX.RISK]?.answered;
  const question3Visible = answers[CONSTANTS.REDUX.TREATMENT_PLAN]?.answered;
  const question4Visible = answers[CONSTANTS.REDUX.INITIAL_AGE]?.answered;
  const [result, setResult] = useState(null);
  const [egfrError, setEgfrError] = useState('');
  const [ageError, setAgeError] = useState('');

  const [interactedFields, setInteractedFields] = useState(() => {
    const initialInteracted = new Set();
    Object.entries(answers).forEach(([key, data]) => {
      if (data?.answered) {
        initialInteracted.add(key);
      }
    });
    return initialInteracted;
  });

  // Trigger initial form validation to ensure visibility logic works with pre-loaded values
  useEffect(() => {
    const currentValues = watch();
    const structured = {};
    Object.entries(currentValues).forEach(([key, val]) => {
      let answered;

      if (
        key === CONSTANTS.REDUX.INITIAL_AGE ||
        key === CONSTANTS.REDUX.TREATMENT_TIMING
      ) {
        answered = typeof val === 'number' && !isNaN(val) && val > 0;
      } else {
        answered = val !== '' && val !== undefined && val !== null;
      }

      structured[key] = { value: val, answered };
    });

    updateAnswers(structured);
  }, []); // Run once on mount

  useEffect(() => {
    const hasRequiredFields = answers[CONSTANTS.REDUX.INITIAL_AGE]?.answered;

    if (hasRequiredFields) {
      const valuesForCalc = {
        [CONSTANTS.REDUX.RISK]: answers[CONSTANTS.REDUX.RISK]?.value,
        [CONSTANTS.REDUX.TREATMENT_PLAN]:
          answers[CONSTANTS.REDUX.TREATMENT_PLAN]?.value,
        [CONSTANTS.REDUX.TREATMENT_TIMING]:
          answers[CONSTANTS.REDUX.TREATMENT_TIMING]?.value,
      };
      setResult(valuesForCalc);
    } else {
      setResult(null);
    }
  }, [answers]);

  useEffect(() => {
    const subscription = watch((values, { name, type }) => {
      if (name && type === 'change') {
        setInteractedFields((prev) => new Set(prev).add(name));
      }

      const structured = {};
      Object.entries(values).forEach(([key, val]) => {
        let answered;

        if (
          key === CONSTANTS.REDUX.INITIAL_AGE ||
          key === CONSTANTS.REDUX.TREATMENT_TIMING
        ) {
          // For number fields, consider them answered if they have a valid value
          // regardless of interaction (to handle pre-loaded values)
          answered = typeof val === 'number' && !isNaN(val) && val > 0;
        } else {
          answered = val !== '' && val !== undefined && val !== null;
        }

        structured[key] = { value: val, answered };
      });

      updateAnswers(structured);

      const hasRequiredFields =
        structured[CONSTANTS.REDUX.INITIAL_AGE]?.answered;

      if (hasRequiredFields) {
        setResult(values);
      } else {
        setResult(null);
      }
    });

    return () => subscription.unsubscribe();
  }, [watch, updateAnswers, interactedFields]);
  return (
    <>
      <div className="grid-container">
        <div className="padding-vertical-3 text-center questionnaire">
          <form className="grid-x align-center grid-margin-y ">
            {/* Question 1 */}
            <div className="cell medium-7">
              <div className="padding-2 shadow margin-auto">
                <div
                  className="question-heading white-color"
                  style={{ flexDirection: 'row' }}
                >
                  <div
                    className="tooltip-container"
                    style={{ visibility: 'hidden' }}
                  >
                    <img src={infoIcon} alt="" />
                  </div>
                  <div className="bordered rounded form-number">1</div>
                  <ClickableTooltip
                    tooltipText={
                      <span>
                        Risk of progression estimated by validated risk
                        prediction tools. See{' '}
                        <a
                          target="_blank"
                          className="text-underline"
                          href="https://Klinrisk.com"
                          rel="noreferrer"
                        >
                          Klinrisk.com
                        </a>{' '}
                        or{' '}
                        <a
                          target="_blank"
                          className="text-underline"
                          href="https://ckdpc.org/risk-models"
                          rel="noreferrer"
                        >
                          {' '}
                          ckdpc.org/risk-models.
                        </a>
                      </span>
                    }
                  >
                    <img src={infoIcon} alt="Information Button" />
                  </ClickableTooltip>
                </div>
                <fieldset>
                  <legend>{CONSTANTS.QUESTIONNAIRE.LABEL1}</legend>
                  <Controller
                    control={control}
                    defaultValue=""
                    name={CONSTANTS.REDUX.RISK}
                    render={({ field }) => (
                      <div className="grid-x small-up-2">
                        <div className="cell">
                          <label>
                            <img
                              className="margin-bottom-1 display-block margin-auto"
                              src={CONSTANTS.QUESTIONNAIRE.YELLOW_KIDNEY}
                            />
                            <input
                              checked={
                                field.value ===
                                CONSTANTS.QUESTIONNAIRE.INTERMEDIATE_RISK
                              }
                              className="radio"
                              onChange={() =>
                                field.onChange(
                                  CONSTANTS.QUESTIONNAIRE.INTERMEDIATE_RISK
                                )
                              }
                              type="radio"
                              value={CONSTANTS.QUESTIONNAIRE.INTERMEDIATE_RISK}
                            />
                            {CONSTANTS.QUESTIONNAIRE.INTERMEDIATE_RISK}
                            <sup>1,2</sup>
                          </label>
                        </div>
                        <div className="cell">
                          <label>
                            <img
                              className="margin-bottom-1 display-block margin-auto"
                              src={CONSTANTS.QUESTIONNAIRE.RED_KIDNEY}
                            />
                            <input
                              checked={
                                field.value ===
                                CONSTANTS.QUESTIONNAIRE.HIGH_RISK
                              }
                              className="radio"
                              onChange={() =>
                                field.onChange(
                                  CONSTANTS.QUESTIONNAIRE.HIGH_RISK
                                )
                              }
                              type="radio"
                              value={CONSTANTS.QUESTIONNAIRE.HIGH_RISK}
                            />
                            {CONSTANTS.QUESTIONNAIRE.HIGH_RISK}
                            <sup>1,2</sup>
                          </label>
                        </div>
                      </div>
                    )}
                  />
                </fieldset>
              </div>
            </div>

            {/* Question 2 */}
            {question2Visible && (
              <div className="cell medium-7">
                <div className="form-field padding-2 shadow margin-auto">
                  <div className="question-heading white-color">
                    <div className="bordered rounded form-number">2</div>
                  </div>
                  <fieldset>
                    <legend>{CONSTANTS.QUESTIONNAIRE.LABEL2}</legend>
                    <Controller
                      control={control}
                      defaultValue=""
                      name={CONSTANTS.REDUX.TREATMENT_PLAN}
                      render={({ field }) => (
                        <div className="grid-x grid-margin-x">
                          {/* Left column - option 1 */}
                          <div className="cell small-6 margin-bottom-1">
                            <label className="flex-container align-top text-left">
                              <input
                                checked={
                                  field.value ===
                                  CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR
                                }
                                className="radio margin-right-1"
                                onChange={() =>
                                  field.onChange(
                                    CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR
                                  )
                                }
                                type="radio"
                                value={CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR}
                              />
                              <span style={{ lineHeight: '18px' }}>
                                {CONSTANTS.QUESTIONNAIRE.RAAS_INHIBITOR}
                              </span>
                            </label>
                          </div>
                          {/* Right column - option 4 */}
                          <div className="cell small-6 margin-bottom-1">
                            <label className="flex-container align-top text-left">
                              <input
                                checked={
                                  field.value ===
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_GLP1RA
                                }
                                className="radio margin-right-1"
                                onChange={() =>
                                  field.onChange(
                                    CONSTANTS.QUESTIONNAIRE
                                      .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_GLP1RA
                                  )
                                }
                                type="radio"
                                value={
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_GLP1RA
                                }
                              />
                              <span style={{ lineHeight: '18px' }}>
                                {
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_GLP1RA
                                }
                              </span>
                            </label>
                          </div>
                          {/* Left column - option 2 */}
                          <div className="cell small-6 margin-bottom-1">
                            <label className="flex-container align-top text-left">
                              <input
                                checked={
                                  field.value ===
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR
                                }
                                className="radio margin-right-1"
                                onChange={() =>
                                  field.onChange(
                                    CONSTANTS.QUESTIONNAIRE
                                      .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR
                                  )
                                }
                                type="radio"
                                value={
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR
                                }
                              />
                              <span style={{ lineHeight: '18px' }}>
                                {
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR
                                }
                              </span>
                            </label>
                          </div>
                          {/* Right column - option 5 */}
                          <div className="cell small-6 margin-bottom-1">
                            <label className="flex-container align-top text-left">
                              <input
                                checked={
                                  field.value ===
                                  CONSTANTS.QUESTIONNAIRE.FOUR_DRUG_THERAPY
                                }
                                className="radio margin-right-1"
                                onChange={() =>
                                  field.onChange(
                                    CONSTANTS.QUESTIONNAIRE.FOUR_DRUG_THERAPY
                                  )
                                }
                                type="radio"
                                value={
                                  CONSTANTS.QUESTIONNAIRE.FOUR_DRUG_THERAPY
                                }
                              />
                              <span style={{ lineHeight: '18px' }}>
                                {CONSTANTS.QUESTIONNAIRE.FOUR_DRUG_THERAPY}
                              </span>
                            </label>
                          </div>
                          {/* Left column - option 3 */}
                          <div className="cell small-6 margin-bottom-1">
                            <label className="flex-container align-top text-left">
                              <input
                                checked={
                                  field.value ===
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_NSMRA
                                }
                                className="radio margin-right-1"
                                onChange={() =>
                                  field.onChange(
                                    CONSTANTS.QUESTIONNAIRE
                                      .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_NSMRA
                                  )
                                }
                                type="radio"
                                value={
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_NSMRA
                                }
                              />
                              <span style={{ lineHeight: '18px' }}>
                                {
                                  CONSTANTS.QUESTIONNAIRE
                                    .RAAS_INHIBITOR_AND_SGLT2_INHIBITOR_AND_NSMRA
                                }
                              </span>
                            </label>
                          </div>
                          {/* Empty cell to maintain grid structure */}
                          <div className="cell small-6"></div>
                        </div>
                      )}
                    />
                  </fieldset>
                </div>
              </div>
            )}

            {/* Questions 3 and 4 - Side by side */}
            {question3Visible && (
              <div className="cell medium-7">
                <div className="grid-x grid-margin-x">
                  {/* Question 3 - Age */}
                  <div className="cell medium-6">
                    <div className="form-field padding-2 shadow margin-auto">
                      <div className="question-heading white-color">
                        <div className="bordered rounded form-number">3</div>
                      </div>
                      <fieldset>
                        <legend>{CONSTANTS.QUESTIONNAIRE.LABEL4}</legend>
                        <Controller
                          control={control}
                          name={CONSTANTS.REDUX.INITIAL_AGE}
                          render={({ field }) => (
                            <div className="grid-x">
                              <div className="cell small-12 flex-container align-center align-middle">
                                <input
                                  id="age-input"
                                  type="number"
                                  className="input-number"
                                  placeholder="age"
                                  value={field.value}
                                  onChange={(e) => {
                                    const value = Number(e.target.value);
                                    field.onChange(value);
                                    if (value < AGE.MIN || value > AGE.MAX) {
                                      setAgeError(
                                        `Enter a value between ${AGE.MIN} and ${AGE.MAX}`
                                      );
                                    } else {
                                      setAgeError('');
                                    }
                                  }}
                                  min={AGE.MIN}
                                  max={AGE.MAX}
                                  step="1"
                                  style={{
                                    MozAppearance: 'textfield',
                                    WebkitAppearance: 'none',
                                    appearance: 'none',
                                  }}
                                  onWheel={(e) => e.currentTarget.blur()}
                                />
                                <div className="text-justify margin-left-1">
                                  Yrs
                                </div>
                              </div>
                              {ageError && (
                                <div className="cell small-12">
                                  <p
                                    style={{
                                      color: '#cc4b37',
                                      fontSize: '0.875rem',
                                      marginTop: '0.5rem',
                                      marginBottom: 0,
                                    }}
                                  >
                                    {ageError}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        />
                      </fieldset>
                    </div>
                  </div>

                  {/* Question 4 - eGFR */}
                  {question4Visible && (
                    <div className="cell medium-6">
                      <div className="form-field padding-2 shadow margin-auto">
                        <div className="question-heading white-color">
                          <div className="bordered rounded form-number">4</div>
                        </div>
                        <fieldset>
                          <legend>{CONSTANTS.QUESTIONNAIRE.LABEL3}</legend>
                          <Controller
                            control={control}
                            defaultValue=""
                            name={CONSTANTS.REDUX.TREATMENT_TIMING}
                            render={({ field }) => (
                              <div className="grid-x">
                                <div className="cell small-12 flex-container align-center align-middle">
                                  <input
                                    id="egfr-input"
                                    type="number"
                                    className="input-number"
                                    placeholder="eGFR value"
                                    value={field.value}
                                    onChange={(e) => {
                                      const value = Number(e.target.value);
                                      field.onChange(value);
                                      if (
                                        value < EGFR.MIN ||
                                        value > EGFR.MAX
                                      ) {
                                        setEgfrError(
                                          `Enter a value between ${EGFR.MIN} and ${EGFR.MAX}`
                                        );
                                      } else {
                                        setEgfrError('');
                                      }
                                    }}
                                    min={EGFR.MIN}
                                    max={EGFR.MAX}
                                    step="1"
                                    style={{
                                      MozAppearance: 'textfield',
                                      WebkitAppearance: 'none',
                                      appearance: 'none',
                                    }}
                                    onWheel={(e) => e.currentTarget.blur()}
                                  />
                                  <div className="text-justify margin-left-1">
                                    mL/min/1.73 m²
                                  </div>
                                </div>
                                {egfrError && (
                                  <div className="cell small-12">
                                    <p
                                      style={{
                                        color: '#cc4b37',
                                        fontSize: '0.875rem',
                                        marginTop: '0.5rem',
                                        marginBottom: 0,
                                      }}
                                    >
                                      {egfrError}
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          />
                        </fieldset>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
      {result && <Graphs />}
    </>
  );
};

export default ConditionalRadioForm;
